import { createSlice } from '@reduxjs/toolkit';
import type { RootState } from '@/store';
import { AuthDto, midleNestApi as api } from '@/store/services/MidleNestApi.service';
import { storageLocal } from '@/utils/storage.utils';
import CustomError from '@/types/CustomError.class';

const slice = createSlice({
  name: 'auth',
  initialState: {
    name: storageLocal.getItem('name'),
    roles: storageLocal.getItem('roles')?.split(','),
    session: storageLocal.getItem('session'),
    accessToken: storageLocal.getItem('accessToken'),
    refreshToken: storageLocal.getItem('refreshToken'),
  } as AuthDto,
  reducers: {
    auth: (state, { payload }) => {
      if (!payload.accessToken) return;
      state.name = payload.name;
      state.roles = payload.roles;
      state.session = payload.session;
      state.accessToken = payload.accessToken;
      state.refreshToken = payload.refreshToken;
      // check base roles
      if (!payload?.roles?.includes('user') && !payload?.roles?.includes('newbie')) {
        throw new CustomError({
          status: 403,
          name: 'Security Error',
          message: 'This account do not have a user access!',
        });
      }
      // TODO write on cookies
      storageLocal.setItem('name', payload?.name);
      storageLocal.setItem('roles', payload?.roles?.join(','));
      storageLocal.setItem('session', payload?.session);
      storageLocal.setItem('accessToken', payload.accessToken);
      storageLocal.setItem('refreshToken', payload.refreshToken);
    },
    changeName: (state, { payload }) => {
      state.name = payload.name;
      storageLocal.setItem('name', payload.name);
    },
    logout: (state) => {
      state.name = '';
      state.roles = [];
      state.session = '';
      state.accessToken = '';
      state.refreshToken = '';
      // TODO delete on cookies
      storageLocal.removeItem('name');
      storageLocal.removeItem('roles');
      storageLocal.removeItem('session');
      storageLocal.removeItem('accessToken');
      storageLocal.removeItem('refreshToken');
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      ({ type, meta }) => {
        return (
          !!type.match('fulfilled') &&
          [
            'authControllerRegister',
            'authControllerLogin',
            'walletControllerAuthenticate',
            'twitterControllerAuthenticate',
            'discordControllerAuthenticate',
            'githubControllerAuthenticate',
            'telegramControllerAuthenticate',
            'googleControllerAuthenticate',
            'cosmosControllerAuthenticate',
            'steamControllerAuthenticate',
            'authControllerVerify',
            'authControllerReset',
            'authControllerUserEdit',
          ].includes(meta?.arg?.endpointName)
        );
      },
      (state, { payload, type }) => {
        slice.caseReducers.auth(state, { payload, type });
      },
    );
    builder.addMatcher(api.endpoints.authControllerLogout.matchFulfilled, (state) => {
      slice.caseReducers.logout(state);
    });
  },
});

export const { auth, logout } = slice.actions;

export const authReducer = slice.reducer;

export const selectAuth = (state: RootState): AuthDto => state.auth;
export const selectAuthStatus = (state: RootState): boolean => !!state.auth.refreshToken;
